import { getMessaging, getToken, onMessage, deleteToken, Messaging } from 'firebase/messaging';
import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../config/firebase.config';
import { registerRiderServiceWorker } from '../utils/registerPwa';
import { soundService } from './sound.service';

export type NotificationType = 
  | 'new-job'
  | 'job-cancelled'
  | 'earnings-update'
  | 'message'
  | 'system'
  | 'shift-reminder'
  | 'promotion';

export interface PushNotification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  data?: Record<string, any>;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
}

class NotificationService {
  private messaging: Messaging | null = null;
  private fcmToken: string | null = null;
  private fcmAuthFailed = false;
  private listeners: Set<(notification: PushNotification) => void> = new Set();

  /**
   * Initialize Firebase Cloud Messaging
   */
  async initialize(): Promise<void> {
    try {
      // Check if browser supports notifications
      if (!('Notification' in window)) {
        console.warn('This browser does not support notifications');
        return;
      }

      // Check if service worker is supported
      if (!('serviceWorker' in navigator)) {
        console.warn('Service Worker not supported');
        return;
      }

      // Initialize messaging
      this.messaging = getMessaging();

      // Listen for foreground messages
      onMessage(this.messaging, (payload) => {
        console.log('📬 Foreground message received:', payload);
        this.handleForegroundMessage(payload);
      });

      console.log('✅ Notification service initialized');
    } catch (error) {
      console.error('❌ Failed to initialize notification service:', error);
    }
  }

  /**
   * Request notification permission and get FCM token
   */
  async requestPermission(): Promise<string | null> {
    try {
      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return null;
      }

      console.log('✅ Notification permission granted');
      
      // Try to get FCM token, but don't fail if it doesn't work
      try {
        return await this.getDeviceToken();
      } catch (fcmError) {
        console.warn('⚠️ FCM token generation failed, using in-app notifications only:', fcmError);
        // Return a placeholder to indicate permission is granted but FCM failed
        return 'in-app-only';
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return null;
    }
  }

  /**
   * Get FCM device token
   */
  async getDeviceToken(): Promise<string | null> {
    if (this.fcmAuthFailed) return null;
    if (!this.messaging) {
      await this.initialize();
      if (!this.messaging) return null;
    }

    try {
      const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
      
      if (!vapidKey || vapidKey === 'YOUR_VAPID_KEY_HERE') {
        console.warn('⚠️ VAPID key not configured. Push notifications will not work.');
        throw new Error('VAPID key not configured');
      }

      // Get token with VAPID key + our /rider/ service worker
      const swRegistration = await registerRiderServiceWorker();
      const token = await getToken(this.messaging, {
        vapidKey: vapidKey,
        ...(swRegistration ? { serviceWorkerRegistration: swRegistration } : {}),
      });

      if (token) {
        console.log('📱 FCM Token generated successfully');
        this.fcmToken = token;
        
        // Save token to Firestore for the current rider
        await this.saveTokenToFirestore(token);
        
        return token;
      } else {
        console.log('No registration token available');
        return null;
      }
    } catch (error: any) {
      console.error('Error getting FCM token:', error);
      if (this.isAuthError(error)) {
        this.fcmAuthFailed = true;
        console.warn('FCM push disabled due to authentication/configuration error; in-app notifications remain available.');
        return null;
      }
      
      // If token was generated for a different Firebase sender, delete it and re-register
      if (error.code === 'messaging/token-subscribe-failed' ||
          this.isSenderIdMismatch(error)) {
        console.warn('⚠️ Stale/wrong FCM token detected. Deleting and re-registering...');
        try {
          await this.deleteToken();
          const freshToken = await getToken(this.messaging!, {
            vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
          });
          if (freshToken) {
            this.fcmToken = freshToken;
            await this.saveTokenToFirestore(freshToken);
            console.log('📱 FCM token re-registered successfully');
            return freshToken;
          }
        } catch (retryError) {
          console.error('FCM token re-registration failed:', retryError);
        }
      }
      
      throw error;
    }
  }

  /**
   * Detect SenderId mismatch or other stale-token errors from the JS SDK
   */
  private isAuthError(error: any): boolean {
    const message = (error?.message || error?.code || '').toString().toLowerCase();
    return message.includes('401') ||
      message.includes('authentication credential') ||
      message.includes('token-subscribe-failed');
  }

  private isSenderIdMismatch(error: any): boolean {
    if (!error) return false;
    const message = (error.message || error.code || '').toString().toLowerCase();
    return message.includes('sender id') ||
           message.includes('senderid') ||
           message.includes('mismatch');
  }

  /**
   * Save FCM token to Firestore
   */
  private async saveTokenToFirestore(token: string): Promise<void> {
    if (!auth.currentUser) return;

    try {
      const riderRef = doc(db, 'riders', auth.currentUser.uid);
      await updateDoc(riderRef, {
        fcmToken: token,
        fcmTokenUpdatedAt: serverTimestamp(),
        notificationsEnabled: true
      });
      console.log('✅ FCM token saved to Firestore');
    } catch (error) {
      console.error('Error saving FCM token:', error);
    }
  }

  /**
   * Delete FCM token (when logging out)
   */
  async deleteToken(): Promise<void> {
    if (!this.messaging || !this.fcmToken) return;

    try {
      await deleteToken(this.messaging);
      
      // Remove from Firestore
      if (auth.currentUser) {
        const riderRef = doc(db, 'riders', auth.currentUser.uid);
        await updateDoc(riderRef, {
          fcmToken: null,
          notificationsEnabled: false
        });
      }

      this.fcmToken = null;
      console.log('✅ FCM token deleted');
    } catch (error) {
      console.error('Error deleting FCM token:', error);
    }
  }

  /**
   * Handle foreground messages
   */
  private handleForegroundMessage(payload: any): void {
    const notification: PushNotification = {
      id: Date.now().toString(),
      type: payload.data?.type || 'system',
      title: payload.notification?.title || 'New Notification',
      body: payload.notification?.body || '',
      data: payload.data,
      timestamp: new Date(),
      read: false,
      actionUrl: payload.data?.actionUrl
    };

    // Show browser notification
    this.showBrowserNotification(notification);

    // Notify listeners (for in-app UI updates)
    this.notifyListeners(notification);

    // Save to local storage for history
    this.saveNotificationToHistory(notification);
  }

  /**
   * Show browser notification
   */
  private showBrowserNotification(notification: PushNotification): void {
    if (Notification.permission !== 'granted') return;

    const notif = new Notification(notification.title, {
      body: notification.body,
      icon: '/go7-logo.png',
      badge: '/go7-badge.png',
      tag: notification.id,
      requireInteraction: notification.type === 'new-job',
      data: notification.data,
      silent: false
    });
    
    // Trigger vibration separately
    if ('vibrate' in navigator) {
      navigator.vibrate(this.getVibrationPattern(notification.type));
    }

    notif.onclick = () => {
      window.focus();
      if (notification.actionUrl) {
        window.location.href = notification.actionUrl;
      }
      notif.close();
    };
  }

  /**
   * Get vibration pattern based on notification type
   */
  private getVibrationPattern(type: NotificationType): number[] {
    switch (type) {
      case 'new-job':
        return [200, 100, 200]; // Double vibration
      case 'message':
        return [100, 50, 100]; // Quick double tap
      case 'earnings-update':
        return [300]; // Single long vibration
      default:
        return [100]; // Single short vibration
    }
  }

  /**
   * Subscribe to notifications
   */
  subscribe(callback: (notification: PushNotification) => void): () => void {
    this.listeners.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all listeners
   */
  private notifyListeners(notification: PushNotification): void {
    // Sound is driven from here because this is the ONE choke point every
    // notification passes through — foreground push (handleForegroundMessage)
    // and in-app events (trigger) alike. A `new-job` notification starts a
    // repeating alert; everything else plays a single tone.
    soundService.onNotification(notification.type);

    this.listeners.forEach(callback => callback(notification));
  }

  /**
   * Save notification to local storage
   */
  private saveNotificationToHistory(notification: PushNotification): void {
    try {
      const history = this.getNotificationHistory();
      history.unshift(notification);
      
      // Keep only last 50 notifications
      const trimmedHistory = history.slice(0, 50);
      
      localStorage.setItem('notification-history', JSON.stringify(trimmedHistory));
    } catch (error) {
      console.error('Error saving notification to history:', error);
    }
  }

  /**
   * Get notification history from local storage
   */
  getNotificationHistory(): PushNotification[] {
    try {
      const history = localStorage.getItem('notification-history');
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('Error reading notification history:', error);
      return [];
    }
  }

  /**
   * Clear notification history
   */
  clearHistory(): void {
    soundService.stopAlert();
    localStorage.removeItem('notification-history');
  }

  /**
   * Mark notification as read
   */
  markAsRead(notificationId: string): void {
    // Opening the alert counts as acknowledging it — stop the ring.
    soundService.stopAlert();
    try {
      const history = this.getNotificationHistory();
      const updated = history.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      );
      localStorage.setItem('notification-history', JSON.stringify(updated));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  /**
   * Get unread count
   */
  getUnreadCount(): number {
    const history = this.getNotificationHistory();
    return history.filter(n => !n.read).length;
  }

  /**
   * Trigger an in-app notification programmatically.
   * Use this whenever a key event happens inside the app
   * (job accepted, delivery complete, earnings credited, etc.)
   */
  trigger(
    type: NotificationType,
    title: string,
    body: string,
    data?: Record<string, any>
  ): void {
    const notification: PushNotification = {
      id: `${type}-${Date.now()}`,
      type,
      title,
      body,
      data,
      timestamp: new Date(),
      read: false
    };

    this.showBrowserNotification(notification);
    this.notifyListeners(notification);
    this.saveNotificationToHistory(notification);
  }

  /**
   * Send test notification (for testing purposes)
   */
  async sendTestNotification(): Promise<void> {
    const testNotification: PushNotification = {
      id: Date.now().toString(),
      type: 'system',
      title: 'Test Notification',
      body: 'This is a test notification from GO7 Rider App',
      timestamp: new Date(),
      read: false
    };

    this.handleForegroundMessage({
      notification: {
        title: testNotification.title,
        body: testNotification.body
      },
      data: {
        type: testNotification.type
      }
    });
  }

  /**
   * Initialize messaging and request push permission when still default.
   * Safe to call on every approved-rider login — won't re-prompt if denied.
   */
  async ensurePushSetup(): Promise<void> {
    if (!this.isSupported()) return;

    await this.initialize();

    if (Notification.permission === 'granted') {
      // Always attempt token refresh on login to recover from stale tokens
      try {
        await this.getDeviceToken();
      } catch {
        console.warn('FCM token refresh skipped — in-app notifications still work');
      }
      return;
    }

    if (Notification.permission === 'default') {
      await this.requestPermission();
    }
  }

  /**
   * Check if notifications are supported
   */
  isSupported(): boolean {
    return 'Notification' in window && 'serviceWorker' in navigator;
  }

  /**
   * Check if notifications are enabled
   */
  isEnabled(): boolean {
    return Notification.permission === 'granted';
  }

  /**
   * Get current permission status
   */
  getPermissionStatus(): NotificationPermission {
    return Notification.permission;
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
